import React, { useEffect, useState } from "react"
import TagManager from "react-gtm-module"

import { graphql, Script } from "gatsby"
import { DatoCmsArticle, DatoCmsSlugsConfiguration, PniPageQuery } from "../../../graphql-types"

import * as config from "../../../config"
import * as styles from "./pni.module.scss"
import * as stylesNewsletter from "../../components/newsletter/newsletter.module.scss"
import { StringParam, useQueryParam } from "use-query-params"
import Layout from "../../components/layout/layout"
import SEO from "../../components/seo"
import HrefLangLinks from "../../components/layout/href-lang-links/href-lang-links"
import Cover from "./cover/cover"
import Articles from "./articles/articles"
import useSlugValue from "../../hooks/use-slug-value"
import Banner from "./banner/banner"

type PNIProps = {
  data: PniPageQuery
  pageContext: {
    locale: string
    defaultLocale: string
    slugs: DatoCmsSlugsConfiguration
  }
}

enum SearchState {
  NoSearch,
  Searching,
  Success,
  Error,
}

const PNI: React.FC<PNIProps> = ({ data, pageContext }) => {
  const displayMessage = (message): JSX.Element => <p className={styles.searchStatusMessage}>{message}</p>

  const filterArticles = (filteredArticles: []) => {
    setArticles(data.datoCmsPniPage.articlesOrderByDate ? sortArticlesByDate(filteredArticles) : filteredArticles)
    setSearchResultsArticles(
      data.datoCmsPniPage.articlesOrderByDate ? sortArticlesByDate(filteredArticles) : filteredArticles
    )
  }

  const changeCurrentPage = newPageNumber => setCurrentPage(newPageNumber)

  /**
   * Sort a list of articles from newer to older.
   * @param articles
   */
  const sortArticlesByDate = (art): [] => {
    return art.slice().sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }

  const stringRemoveAccents = (str): string => {
    return str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
  }

  const searchArticles = searchTerm => {
    const terms = stringRemoveAccents(searchTerm)
    const termsSplit = terms.split(" ")
    const matchArticles: Array<DatoCmsArticle> = []

    const searchWord = word => {
      const pushArticle = art => {
        if (!matchArticles.includes(art)) {
          if (
            (art.articleTitle && stringRemoveAccents(art.articleTitle).indexOf(word.toString()) !== -1) ||
            (art.author && stringRemoveAccents(art.author).indexOf(word.toString()) !== -1) ||
            (art.category.label && stringRemoveAccents(art.category.label).indexOf(word.toString()) !== -1) ||
            (art.description && stringRemoveAccents(art.description).indexOf(word.toString()) !== -1) ||
            (art.authorPosition && stringRemoveAccents(art.authorPosition).indexOf(word.toString()) !== -1)
          ) {
            if (matchArticles.indexOf(art) < 0) matchArticles.push(art)
          }
          art.tags.forEach(tag => {
            if (
              stringRemoveAccents(tag.label).indexOf(word.toString()) !== -1 ||
              stringRemoveAccents(tag.label).includes(word.toString()) ||
              stringRemoveAccents(tag.label) === word.toString()
            ) {
              if (matchArticles.indexOf(art) < 0) matchArticles.push(art)
            }
          })
        }
      }

      pushArticle(data.datoCmsPniPage.highlightedArticle)
      articles.forEach(art => pushArticle(art))
    }

    termsSplit.forEach(word => {
      if (word.length >= 3) {
        searchWord(word)
      }
    })
    searchWord(terms)

    setStaticSearchResultsArticles(sortArticlesByDate(matchArticles))
    setSearchResultsArticles(sortArticlesByDate(matchArticles))
  }

  useEffect(() => {
    if (config.google_tag_manager_id) {
      TagManager.initialize({ gtmId: config.google_tag_manager_id })
      TagManager.dataLayer({
        dataLayer: {
          environment: process.env.NODE_ENV,
          language: pageContext.locale,
          pageTemplate: searchTerms ? "search result" : "categorie",
        },
      })
    }
  }, [])

  const siteSearchUrl = "https://unpkg.com/datocms-search@0.1.8/dist/datocms-search.base.js"

  const [siteSearchLoaded, setSiteSearchLoaded] = useState(typeof window !== "undefined" && !!window["DatoCmsSearch"])
  const [client, setClient] = useState(null)
  const [searchResults, setSearchResults] = useState([])
  const [searchResultsArticles, setSearchResultsArticles] = useState([])
  const [staticSearchResultsArticles, setStaticSearchResultsArticles] = useState([])
  const [searchState, setSearchState] = useState(SearchState.NoSearch)
  const [searchTerms, _] = useQueryParam("query", StringParam)
  const [articles, setArticles] = useState(
    data.datoCmsPniPage.articlesOrderByDate
      ? sortArticlesByDate(data.datoCmsPniPage.articles)
      : data.datoCmsPniPage.articles
  )
  const [currentPage, setCurrentPage] = useState(1)
  const breadcrumb = [
    {
      link: data.datoCmsSlugsConfiguration.projectsNewsInsightsPageSlug,
      title: data.datoCmsPniPage.headerTitle,
    },
  ]

  /**
   * Set client when site is loaded
   */
  useEffect(() => {
    if (!siteSearchLoaded) return

    setClient(new window["DatoCmsSearch"]("005efa7f664c7391e38f9007881135", "main"))
  }, [siteSearchLoaded])

  /**
   * If client has been set and search word exist, search for that word.
   */
  useEffect(() => {
    if (!client) {
      return
    }
    if (!searchTerms) {
      setSearchState(SearchState.NoSearch)
      return
    }
    setSearchState(SearchState.Searching)
    client.search(searchTerms).then(
      ({ results }) => {
        searchArticles(searchTerms)
        setSearchResults(results)
        setSearchState(SearchState.Success)
      },
      error => {
        console.error(error)
        setSearchState(SearchState.Error)
      }
    )
  }, [client, searchTerms])

  const pniSlug = useSlugValue(pageContext.slugs._allProjectsNewsInsightsPageSlugLocales, pageContext.locale)

  return (
    <Layout
      pageContext={pageContext}
      currentRoute={pniSlug}
      headerConfig={data.datoCmsHeader}
      footerConfig={data.datoCmsFooter}
      noIndex={!!searchTerms}
    >
      <Script src={siteSearchUrl} onLoad={() => setSiteSearchLoaded(true)} />
      <div className={styles.pni}>
        <Banner
          data={data.datoCmsPniPage}
        />
        <Cover
          data={data.datoCmsPniPage}
          filterArticles={filterArticles}
          articles={
            searchTerms && searchResults && searchState === SearchState.Success && searchResults.length > 0
              ? staticSearchResultsArticles
              : data.datoCmsPniPage.articles
          }
          searchRequestSlug={
            pageContext.locale === pageContext.defaultLocale ? pniSlug : `${pageContext.locale}/${pniSlug}`
          }
          searchTerms={searchTerms}
          changeCurrentPage={changeCurrentPage}
          displayFilters={!(searchTerms && searchState === SearchState.Success && searchResults.length === 0)}
        />
        {searchState === SearchState.NoSearch && (
          <Articles
            articles={articles}
            data={data.datoCmsPniPage}
            projectsNewsInsightsPageSlug={data.datoCmsSlugsConfiguration.projectsNewsInsightsPageSlug}
            currentPage={currentPage}
            changeCurrentPage={changeCurrentPage}
            displayHighlightedArticle={true}
          />
        )}
        {searchState === SearchState.Searching && displayMessage(data.datoCmsPniPage.searchInProgressMessage)}
        {searchState === SearchState.Error && displayMessage(data.datoCmsPniPage.searchErrorMessage)}
        {((searchTerms && searchState === SearchState.Success && searchResultsArticles.length === 0) ||
          (searchResultsArticles.length === 0 && articles.length === 0)) &&
          displayMessage(data.datoCmsPniPage.noResultsFoundMessage)}
        {searchTerms &&
          searchResults &&
          searchState === SearchState.Success &&
          searchResultsArticles.length > 0 &&
          articles.length > 0 && (
            <Articles
              articles={searchResultsArticles}
              data={data.datoCmsPniPage}
              projectsNewsInsightsPageSlug={data.datoCmsSlugsConfiguration.projectsNewsInsightsPageSlug}
              currentPage={currentPage}
              changeCurrentPage={changeCurrentPage}
              displayHighlightedArticle={false}
            />
          )}
      </div>
      {/*{data.datoCmsHomePage.newsletterIsActive && <Newsletter data={data.datoCmsHomePage} />}*/}
      <section className={stylesNewsletter.container}>
        <div className={stylesNewsletter.iframe} id="newsletter">
          {pageContext.locale === pageContext.defaultLocale ? (
            <iframe
              title="Landing Page"
              src="https://f.inno.keolis.com/f/l/?q=BSLqpzmY16RtCSEShgohLdWayZ7VLugElOIBd2us19BPYmcYHtWnKpZ_ghOHlwepKS8vEt2XF5fgJVpUTxggfGgh-u3ecnw4-iMJYaJL4PUWZ0fQ30PVivkfxCsaGeXFURFY1n9rGUovvmn4gjhnLYdWzv1jAMrtWxpTPOZmjilw2he_luP-qSjZukWgwL7LDF2AE08YfAU1ZfHF9nGi1cx52MZS_6zv-5JbE27ctJ5w4kkP_keKx-Cw5tdC2TvE"
            />
          ) : (
            <iframe
              title="Landing Page"
              src="https://f.inno.keolis.com/f/l/?q=BSLqpzmY16RtCSEShgohLTb9NHftVXSchI5U5X7tFck1wSvnK6Zju4LY9embQyGwFPQdea4JaeTvJx99yXHXclaUhOvyWW_WeYDVvlSeXvuqssfSD2RlonLzJpgW8R20QGbQNQqp7rusHzAE9gDZIjcD53XmVjJPoxHfCcN2-4__pURFfpdMI14KVPoBL9VUVewRCg07AwrLLZ33NS3YaE_sOSd8bVgxSs-DXSo3dHT9AsYS-1K3UrI9nal2LqNc"
            />
          )}
        </div>
      </section>
    </Layout>
  )
}

export const Head: React.FC<PNIProps> = ({ data, pageContext }) => {
  const isBrowser = typeof window !== "undefined"
  const [searchTerms, _] = useQueryParam("query", StringParam)
  const defaultDomain = "https://innovation.keolis.com"
  return (
    isBrowser && (
      <>
        <SEO
          title={
            !searchTerms
              ? data.datoCmsPniPage.title
              : `${data.datoCmsPniPage.titleSearchPagePrefix} - ${searchTerms} | ${data.datoCmsPniPage.titleSearchPageSuffix}`
          }
          description={data.datoCmsPniPage.metaDescription}
          locale={pageContext.locale}
          noindex={data.datoCmsPniPage.noindex}
        />
        {typeof window !== "undefined" && (
          <HrefLangLinks
            defaultDomain={window.location.origin}
            defaultLocale={pageContext.defaultLocale}
            slugs={[pageContext.slugs._allProjectsNewsInsightsPageSlugLocales]}
          />
        )}
      </>
    )
  )
}

export const fragment = graphql`
  fragment SearchFields on DatoCmsPniPage {
    searchInProgressMessage
    searchErrorMessage
    noResultsFoundMessage
  }
`

export const pageQuery = graphql`
  query PniPage($locale: String!) {
    datoCmsGlobalConfiguration {
      locales
    }
    datoCmsSlugsConfiguration(locale: $locale) {
      projectsNewsInsightsPageSlug
    }
    datoCmsHeader(locale: $locale) {
      ...HeaderFields
    }
    datoCmsFooter(locale: $locale) {
      ...FooterFields
    }
    datoCmsPniPage(locale: $locale) {
      title
      titleSearchPagePrefix
      titleSearchPageSuffix
      metaDescription
      noindex
      ...HeaderPNIFields
      ...PniCoverFields
      ...PniArticlesFields
      ...SearchFields
      ...PniBannerFields
      ...PniCardFields
    }
    datoCmsHomePage(locale: $locale) {
      ...NewsletterFields
    }
  }
`

export default PNI
